import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CLAIM_STATUS, REPAIR_STATUS, StatusChip, Thread } from "@/components/common/Thread";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/common/DatePicker";
import { api, formatApiError } from "@/lib/api";
import { money } from "@/lib/format";

const eur = (v) => money(v || 0, "EUR", "fr-FR", { maximumFractionDigits: 2 });
const useGet = (key, url) => useQuery({ queryKey: key, queryFn: () => api.get(url).then((r) => r.data) });
const Empty = ({ text }) => <p className="card-surface p-6 text-sm text-muted-foreground">{text}</p>;
const Stat = ({ label, value, testid }) => (
  <div className="card-surface tone-wash tone-mint p-5" data-testid={testid}><div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div><div className="num mt-1 text-3xl font-bold">{value}</div></div>
);

const useAct = (keys) => {
  const qc = useQueryClient();
  return async (fn, msg) => { try { await fn(); toast.success(msg); keys.forEach((k) => qc.invalidateQueries({ queryKey: [k] })); } catch (e) { toast.error(formatApiError(e)); } };
};

const QuoteForm = ({ r, act }) => {
  const [v, setV] = useState({ amount: r.quote?.amount ?? "", visit_date: "", message: "" });
  return (
    <form className="grid gap-2 sm:grid-cols-[8rem_12rem_1fr_auto]" data-testid={`pro-quote-form-${r.id}`}
      onSubmit={(e) => { e.preventDefault(); act(() => api.post(`/pro/repairs/${r.id}/quote`, { amount: Number(v.amount), visit_date: v.visit_date || null, message: v.message || null }), "Devis envoyé"); }}>
      <Input type="number" min="1" step="0.01" value={v.amount} onChange={(e) => setV({ ...v, amount: e.target.value })} placeholder="Montant €" required data-testid={`pro-quote-amount-${r.id}`} />
      <DatePicker value={v.visit_date} onChange={(e) => setV({ ...v, visit_date: e.target.value })} placeholder="Date d'intervention" data-testid={`pro-quote-date-${r.id}`} />
      <Input value={v.message} onChange={(e) => setV({ ...v, message: e.target.value })} placeholder="Message (facultatif)" data-testid={`pro-quote-message-${r.id}`} />
      <Button type="submit" className="rounded-full" data-testid={`pro-quote-submit-${r.id}`}>Envoyer le devis</Button>
    </form>
  );
};

const ProRepairCard = ({ r }) => {
  const act = useAct(["pro-repairs", "pro-overview"]);
  const status = (s, msg) => act(() => api.post(`/pro/repairs/${r.id}/status`, { status: s }), msg);
  const a = r.asset || {};
  return (
    <li className="card-surface space-y-3 p-5" data-testid={`pro-repair-${r.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs text-muted-foreground">{r.ref}</span>
        <span className="min-w-0 flex-1 truncate font-semibold">{a.name} · {[a.brand, a.model].filter(Boolean).join(" ")}</span>
        <StatusChip map={REPAIR_STATUS} status={r.status} testid={`pro-repair-status-${r.id}`} />
      </div>
      <div className="text-xs text-muted-foreground">Client : {r.requester} · {r.household_name}{a.serial_number && ` · N° série ${a.serial_number}`}{a.purchase_date && ` · acheté le ${new Date(a.purchase_date).toLocaleDateString("fr-FR")}`}{a.warranty_end && ` · garantie jusqu'au ${new Date(a.warranty_end).toLocaleDateString("fr-FR")}`}</div>
      <p className="text-sm">{r.description}</p>
      {r.quote && <div className="text-sm">Devis : <b className="num">{eur(r.quote.amount)}</b></div>}
      {["new", "quoted", "declined"].includes(r.status) && <QuoteForm r={r} act={act} />}
      <div className="flex flex-wrap gap-2">
        {r.status === "accepted" && <Button size="sm" className="rounded-full" onClick={() => status("in_progress", "Intervention démarrée")} data-testid={`pro-repair-start-${r.id}`}>Démarrer l'intervention</Button>}
        {["accepted", "in_progress"].includes(r.status) && <Button size="sm" variant="outline" className="rounded-full" onClick={() => status("done", "Intervention terminée")} data-testid={`pro-repair-done-${r.id}`}>Marquer terminée</Button>}
        {["new", "quoted"].includes(r.status) && <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={() => status("refused", "Demande refusée")} data-testid={`pro-repair-refuse-${r.id}`}>Refuser</Button>}
      </div>
      <Thread messages={r.messages} mine="pro" testid={`pro-repair-thread-${r.id}`} onSend={async (text) => { await api.post(`/pro/repairs/${r.id}/messages`, { text }); act(async () => {}, "Message envoyé"); }} />
    </li>
  );
};

export const ProRepairs = ({ statuses, empty }) => {
  const { data = [] } = useGet(["pro-repairs"], "/pro/repairs");
  const list = statuses ? data.filter((r) => statuses.includes(r.status)) : data;
  return list.length ? <ul className="space-y-4" data-testid="pro-repairs-list">{list.map((r) => <ProRepairCard key={r.id} r={r} />)}</ul> : <Empty text={empty || "Aucune demande reçue."} />;
};

export const ProBilling = () => {
  const { data = [] } = useGet(["pro-repairs"], "/pro/repairs");
  const done = data.filter((r) => r.status === "done");
  return (
    <div className="space-y-4" data-testid="pro-billing">
      <div className="grid gap-4 sm:grid-cols-2"><Stat label="Interventions terminées" value={done.length} /><Stat label="Chiffre d'affaires" value={eur(done.reduce((s, r) => s + (r.quote?.amount || 0), 0))} testid="pro-revenue" /></div>
      {done.length ? <ul className="card-surface divide-y divide-border px-5">{done.map((r) => <li key={r.id} className="flex justify-between py-3 text-sm"><span>{r.ref} · {r.asset?.name} · {r.requester}</span><span className="num">{eur(r.quote?.amount)}</span></li>)}</ul> : <Empty text="Aucune intervention facturable." />}
    </div>
  );
};

export const ProRegistrations = ({ view }) => {
  const { data } = useGet(["pro-registrations"], "/pro/registrations");
  if (!data) return null;
  if (view === "models") return (
    <div className="space-y-4" data-testid="pro-catalogue">
      <div className="grid gap-4 sm:grid-cols-2"><Stat label="Produits enregistrés" value={data.registrations.length} /><Stat label="Demandes SAV ouvertes" value={data.open_repairs} /></div>
      {data.by_model.length ? <ul className="card-surface divide-y divide-border px-5">{data.by_model.map((m) => <li key={m.model} className="flex justify-between py-3 text-sm"><span>{m.model}</span><span className="num font-bold">{m.count}</span></li>)}</ul> : <Empty text="Aucun produit enregistré." />}
    </div>
  );
  return data.registrations.length ? (
    <ul className="card-surface divide-y divide-border px-5" data-testid="pro-registrations-list">
      {data.registrations.map((r) => <li key={r.id} className="flex flex-wrap justify-between gap-2 py-3 text-sm"><span>{r.product.name} · {r.product.model}{r.product.serial_number && ` · ${r.product.serial_number}`}</span><span className="text-xs text-muted-foreground">Achat {r.product.purchase_date ? new Date(r.product.purchase_date).toLocaleDateString("fr-FR") : "—"} · garantie {r.warranty_end ? new Date(r.warranty_end).toLocaleDateString("fr-FR") : "—"}</span></li>)}
    </ul>
  ) : <Empty text="Aucun produit enregistré." />;
};

export const ProContracts = () => {
  const { data = [] } = useGet(["pro-contracts"], "/pro/contracts");
  return data.length ? (
    <ul className="card-surface divide-y divide-border px-5" data-testid="pro-contracts-list">
      {data.map((c) => <li key={c.id} className="flex flex-wrap justify-between gap-2 py-3 text-sm" data-testid={`pro-contract-${c.id}`}><span><b>{c.household_name}</b> · {c.kind} · n° {c.number}</span><span className="text-xs text-muted-foreground">{c.covered_count} équipement(s) · {c.premium_month != null ? `${eur(c.premium_month)}/mois` : "—"}</span></li>)}
    </ul>
  ) : <Empty text="Aucun assuré." />;
};

const ClaimCard = ({ x }) => {
  const act = useAct(["pro-claims", "pro-overview"]);
  const [amount, setAmount] = useState(x.amount_claimed ?? "");
  const set = (status, msg, extra = {}) => act(() => api.post(`/pro/claims/${x.id}/status`, { status, ...extra }), msg);
  return (
    <li className="card-surface space-y-3 p-5" data-testid={`pro-claim-${x.id}`}>
      <div className="flex flex-wrap items-center gap-2"><span className="font-mono text-xs text-muted-foreground">{x.ref}</span><span className="flex-1 font-semibold">{x.household_name} · {x.asset_name || "Sinistre"}</span><StatusChip map={CLAIM_STATUS} status={x.status} testid={`pro-claim-status-${x.id}`} /></div>
      <div className="text-xs text-muted-foreground">Contrat {x.contract_number} · survenu le {new Date(x.incident_date).toLocaleDateString("fr-FR")} · déclaré {x.amount_claimed != null ? eur(x.amount_claimed) : "—"}{x.amount_approved != null && ` · accordé ${eur(x.amount_approved)}`}</div>
      <p className="text-sm">{x.description}</p>
      <div className="flex flex-wrap items-center gap-2">
        {x.status === "submitted" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => set("under_review", "Dossier en instruction")} data-testid={`pro-claim-review-${x.id}`}>Mettre en instruction</Button>}
        {["submitted", "under_review"].includes(x.status) && <>
          <Input type="number" min="0" step="0.01" className="w-32" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Montant €" data-testid={`pro-claim-amount-${x.id}`} />
          <Button size="sm" className="rounded-full" onClick={() => set("approved", "Sinistre accepté", { amount_approved: Number(amount) })} disabled={amount === ""} data-testid={`pro-claim-approve-${x.id}`}>Accepter</Button>
          <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={() => set("rejected", "Sinistre refusé")} data-testid={`pro-claim-reject-${x.id}`}>Refuser</Button>
        </>}
        {x.status === "approved" && <Button size="sm" className="rounded-full" onClick={() => set("paid", "Indemnisation versée")} data-testid={`pro-claim-pay-${x.id}`}>Marquer indemnisé</Button>}
      </div>
      <Thread messages={x.messages} mine="pro" testid={`pro-claim-thread-${x.id}`} onSend={async (text) => { await api.post(`/pro/claims/${x.id}/messages`, { text }); act(async () => {}, "Message envoyé"); }} />
    </li>
  );
};

export const ProClaims = () => {
  const { data = [] } = useGet(["pro-claims"], "/pro/claims");
  return data.length ? <ul className="space-y-4" data-testid="pro-claims-list">{data.map((x) => <ClaimCard key={x.id} x={x} />)}</ul> : <Empty text="Aucun sinistre déclaré." />;
};

export const ProBankCustomers = () => {
  const { data = [] } = useGet(["pro-bank"], "/pro/bank/customers");
  return data.length ? (
    <ul className="card-surface divide-y divide-border px-5" data-testid="pro-bank-customers">
      {data.map((c) => <li key={c.name} className="flex flex-wrap justify-between gap-2 py-3 text-sm"><span className="font-semibold">{c.name}</span><span className="text-xs text-muted-foreground">{c.accounts} compte(s) · encours <b className="num">{eur(c.total_balance)}</b></span></li>)}
    </ul>
  ) : <Empty text="Aucun client relié." />;
};

const LABELS = { new: "Nouvelles", quoted: "Devis envoyés", accepted: "Acceptées", in_progress: "En cours", done: "Terminées",
  submitted: "Déclarés", under_review: "En instruction", approved: "Acceptés", paid: "Indemnisés", rejected: "Refusés" };

export const ProOverview = () => {
  const { data } = useGet(["pro-overview"], "/pro/overview");
  if (!data) return null;
  const counts = data.repairs || data.claims;
  return (
    <div className="space-y-4" data-testid="pro-overview">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {data.contracts != null && <Stat label="Contrats actifs" value={data.contracts} testid="pro-stat-contracts" />}
        {data.registrations != null && <Stat label="Produits enregistrés" value={data.registrations} testid="pro-stat-registrations" />}
        {data.customers != null && <><Stat label="Clients reliés" value={data.customers} testid="pro-stat-customers" /><Stat label="Comptes" value={data.accounts} /></>}
        {data.revenue_done != null && <Stat label="CA réalisé" value={eur(data.revenue_done)} testid="pro-stat-revenue" />}
        {counts && Object.entries(counts).map(([k, v]) => <Stat key={k} label={LABELS[k] || k} value={v} testid={`pro-stat-${k}`} />)}
      </div>
      <p className="text-xs text-muted-foreground">Vous ne voyez que les dossiers qui vous sont adressés ou explicitement partagés par les foyers.</p>
    </div>
  );
};
